import { Vector as VectorSource } from 'ol/source'
import { Vector as VectorLayer } from 'ol/layer'
import { Geometry, LineString, Point } from 'ol/geom'
import GeoJSON from 'ol/format/GeoJSON.js'
import {
    processRouteForMap,
    ProcessRouteOptions,
    routeLineStyles,
} from '../utils/map-utils'
import { Circle, Fill, Stroke, Style } from 'ol/style'
import { Map as MGMap } from '@maersk-global/map-layers'
import { VoyageRoutingModel, VoyageRoute } from '../models/voyage-routing-model'
import { Feature } from 'ol'
import { Modify } from 'ol/interaction'
import { MapBrowserEvent } from 'ol'
import { PlanningLayersColors } from './types/plan-layer.types'
import { VesselPlanningModel } from '../models/vessel-planning-model'
import { toLonLat } from 'ol/proj'
import { deepCopy, deepEqual } from '../utils/helpers'
import { WeatherModel } from '../models/weather-model'
import { RouteWeatherWorker, WarningPoint } from '../utils/route-weather-worker'
import { SuggestedRouteEvents } from '../../local_modules/voyage-suggested-routes/src/types'
import { formatTime } from './utils'

const _PROJECTION = (<any>window)['appenv'].projection

export enum PolarRouteLayerEvents {
    PolarCoordinateCaptured = 'polar-coordinate-captured',
}

export class PolarRouteLayer extends EventTarget {
    map: MGMap | null = null
    private _modify: Modify | null = null
    private _layers: VectorLayer<VectorSource<Geometry>>[] = []
    private _routesVectorSource: VectorSource<Geometry> | null = null
    private _notPassedRoutesVectorSource: VectorSource<Geometry> | null = null
    private _warningPoints: WarningPoint[] = [];
    private _waypointFeatures: Feature<Geometry>[] = [];
    private _currentRoute: VoyageRoute | null = null;
    
    constructor() {
        super()
        this._onRoutesChanged = this._onRoutesChanged.bind(this)
        this.plotRoutes = this.plotRoutes.bind(this)
        this.captureCoordinate = this.captureCoordinate.bind(this)
        this.clearRoutes = this.clearRoutes.bind(this)
    }

    assignMap(map: MGMap) {
        this.map = map
    }

    get layers() {
        return this._layers
    }

    async initialize() {
        this._routesVectorSource = new VectorSource()
        this._notPassedRoutesVectorSource = new VectorSource()

        const mainLayer = new VectorLayer({
            source: this._routesVectorSource,
            visible: false,
        })

        const selectableLayer = new VectorLayer({
            source: this._notPassedRoutesVectorSource,
            visible: false,
            opacity: 0,
            style: null,
        })

        this._layers.push(mainLayer)
        this._layers.push(selectableLayer)

        VoyageRoutingModel!.addEventListener(
            'routes-changed',
            this._onRoutesChanged,
        )
        WeatherModel!.addEventListener('weather-updated', this.plotRoutes);
        VoyageRoutingModel!.addEventListener(SuggestedRouteEvents.WeatherLimitsChanged, this.plotRoutes);
        this._getVesselCharacteristics()
    }

    clearRoutes() {
        try {
            this._routesVectorSource!.clear()
            this._notPassedRoutesVectorSource!.clear()
            this._waypointFeatures = []
            this._currentRoute = null
        } catch (error) {
            console.warn(error)
        }
    }

    private colors: PlanningLayersColors = {
        defaultColor: '#0EA299',
        passedColor: '#A3A3A3',
        uiRouteColors: [],
        warningColor: '#FFB302',
        avoidColor: '#FF0000',
        waypointFillColor: '',
        parametricRollColor: '#FE92E9',
        heavyRollingColor: "#8454D6",
        transverseAccelerationColor: "#BF6410",
    }

    private addInteractions() {
        this._modify = new Modify({
            source: this._notPassedRoutesVectorSource!,
            style: new Style({
                image: new Circle({
                    radius: 5,
                    fill: new Fill({ color: '#003D6D' }),
                }),
            }),
            // @ts-ignore - OpenLayers typescript definitions are not complete
            handleDownEvent: () => false,
            handleDragEvent: () => false,
        })

        /**
         * making sure it doesn't get added when the layer isn't visible
         * otherwise the interaction will show on other layers
         *  */
        const handleLayerVisibilityChange = () => {
            const isLayerVisible = this._layers[1].getVisible()
            if (isLayerVisible) {
                this.map!.addInteraction(this._modify!)
            } else {
                this.map!.removeInteraction(this._modify!)
            }
        }

        handleLayerVisibilityChange()

        this._layers[1].on('change:visible', handleLayerVisibilityChange)

        this.map!.on('pointermove', (event: MapBrowserEvent<UIEvent>) => {
            if (!this._layers[1].getVisible()) return;

            const hovered = this.map?.getFeaturesAtPixel(event.pixel, {
                layerFilter: (layer) => layer === this._layers[1],
            });

            if (hovered && hovered.length > 0) {
                const etaMs = this._currentRoute
                    ? VoyageRoutingModel!.getEtaAtPosition(event.coordinate, this._currentRoute)
                    : null;
                const eta = etaMs ? formatTime(new Date(etaMs).toISOString()) : '';
                for (const f of hovered) {
                    (f as Feature<Geometry>).set('name', eta);
                }
            } else {
                this._notPassedRoutesVectorSource?.getFeatures()
                    .forEach(f => f.unset('name'));
            }
        });

        this.map!.on('singleclick', (event) => {
            if (this._layers[1].getVisible()) {
                this.captureCoordinate(event)
            }
        })
    }

    private captureCoordinate(event: MapBrowserEvent<UIEvent>): void {
        const features = this.map?.getFeaturesAtPixel(event.pixel, {
            layerFilter: (layer) => layer === this._layers[1],
        })

        if (features && features.length > 0) {
            const projectedCoordinate = event.coordinate
            const lonLat = toLonLat(projectedCoordinate, _PROJECTION)

            let hasParametricRisk = false;

            const tolerance = 0.1;
            for (const wp of this._warningPoints) {
                if (
                    Math.abs(wp.coordinates[0] - lonLat[0]) < tolerance &&
                    Math.abs(wp.coordinates[1] - lonLat[1]) < tolerance
                ) {
                    if (wp.warnings.avoid && Object.keys(wp.warnings.avoid).length > 0) {
                        hasParametricRisk = true;
                        break;
                    }
                }
            }

            const etaMs = this._currentRoute
                ? VoyageRoutingModel!.getEtaAtPosition(projectedCoordinate, this._currentRoute)
                : null;
            const nearestEta = etaMs === null ? null : new Date(etaMs).toISOString();

            this.dispatchEvent(
                new CustomEvent(PolarRouteLayerEvents.PolarCoordinateCaptured, {
                    detail: { lonLat, hasParametricRisk, nearestEta },
                }),
            )
        }
    }

    private async plotRoutes() {
        const plotFeatures: Feature<Geometry>[] = []
        const unpassedWaypointsFeatures: Feature<Geometry>[] = []

        const plan = await VesselPlanningModel!.getExecutingPlan()

        if (!plan || plan.routes.length === 0 || plan.selectedRouteIndex < 0)
            {
                this.clearRoutes()
                return;
            }

        let route = deepCopy(plan.routes[plan.selectedRouteIndex]);

        if (WeatherModel!.latestForecastVersion !== route.route_info!.calculationInput.forecast_versions[0] ||
            !deepEqual(route.route_info!.calculationInput.weatherLimits, VoyageRoutingModel!.getWeatherLimits()))
        {
            const weatherWorker = new RouteWeatherWorker(route, VoyageRoutingModel!.getWeatherLimits());
            await weatherWorker.processRoute();
        }

        this._currentRoute = route;

        const geoJSON = {
            type: 'FeatureCollection',
            features: route.route_geometry,
        }

        let features = new GeoJSON().readFeatures(geoJSON)
        const processRouteOptions: ProcessRouteOptions = {
            features: features,
            addWarnings: true,
        }
        features = processRouteForMap(processRouteOptions)

        this._waypointFeatures = [];

        // First pass: collect waypoint Point features.
        for (const feature of features) {
            if (feature.getGeometry() instanceof Point) {
                feature.getGeometry()!.transform('EPSG:4326', _PROJECTION);
                this._waypointFeatures.push(feature);
            }
        }

        // Second pass: render LineString segments.
        for (let feature of features) {
            if (feature.getGeometry() instanceof LineString) {
                ;(feature.getGeometry() as LineString).transform(
                    'EPSG:4326',
                    _PROJECTION,
                )
                if (!feature.get('isPassed')) {
                    unpassedWaypointsFeatures.push(feature)
                }
                feature.setStyle((feature, res) =>
                    routeLineStyles(<Feature<LineString>>feature, res, {
                        ...this.colors,
                    }),
                )

                plotFeatures.push(feature)
            }
        }

        this._routesVectorSource!.addFeatures(plotFeatures)
        this._notPassedRoutesVectorSource!.addFeatures(
            unpassedWaypointsFeatures,
        )
    }

    private async _onRoutesChanged() {
        this.clearRoutes()
        this.plotRoutes()
        this.addInteractions()
    }

    async _getVesselCharacteristics() {
        const result = await VesselPlanningModel!.getVesselCharacteristics()
        if (!result) return
        this._onRoutesChanged()
    }

    
}
